import { getHashObj, setHash, stringIsValidDate } from "@/utils/utils"
import store from "@/vue/planning/store/store"
import { transportPacks } from "@/config/transportPackagePriorities"
import trainPackCode from "@/config/trainPackCode"
import {
	getAllowedOriginsForTrainPack,
	getPreferredTrainOrigin,
	isTrainStationOrigin,
	resolveTrainOriginForPack,
	resolveTrainPackCodeForOrigin,
} from "@/config/trainPackageConfig"

export default {
	readUrl: ({ dispatch }) => {
		dispatch("refreshStateFromHash")

		const hash = window.location.hash.replace("#", "")

		// get hash values
		const hashObj = getHashObj(hash)

		// set hash in case it was changed here
		setHash(hashObj)
	},

	refreshStateFromHash: ({ commit, dispatch, state }) => {
		const hashStr = window.location.hash.replace("#", "")
		const hashObj = getHashObj(hashStr)

		if (hashObj["packCode"]) commit("setPackage", hashObj["packCode"])
		const beginDate = hashObj["BeginDate"]
		const endDate = hashObj["EndDate"]
		const origin = hashObj['origin']
		if (
			beginDate &&
			endDate &&
			stringIsValidDate(hashObj["BeginDate"]) &&
			stringIsValidDate(hashObj["EndDate"])
		) {
			commit("setStartDate", hashObj["BeginDate"])
			commit("setEndDate", hashObj["EndDate"])
			dispatch("refreshDataStoredOnWindow")
		}

		const packCode = hashObj["packCode"]
		if (origin && transportPacks.includes(packCode)) {
			if (trainPackCode.includes(packCode)) {
				const originCode = origin.split("-")[0] || origin
				const allowed = getAllowedOriginsForTrainPack(packCode)
				const originFitsFamily =
					isTrainStationOrigin(originCode) &&
					(!allowed || allowed.includes(originCode))

				if (originFitsFamily) {
					// NL: PFPTT + AMS → PFPTTNL; PFPTTNL + AMSES → PFPTT. UK: PFPTTUK + STP → PFPTT, etc.
					const resolvedPack = resolveTrainPackCodeForOrigin(packCode, originCode)
					if (resolvedPack !== packCode) {
						commit("setPackage", resolvedPack)
						setHash({ packCode: resolvedPack })
					}
					commit("setFlightOrigin", originCode)
				} else {
					const packForOrigin = resolveTrainPackCodeForOrigin(packCode, originCode)
					const validOrigin =
						resolveTrainOriginForPack(packForOrigin, originCode) ||
						getPreferredTrainOrigin(packForOrigin) ||
						originCode
					if (packForOrigin !== packCode) {
						commit("setPackage", packForOrigin)
						setHash({ packCode: packForOrigin })
					}
					commit("setFlightOrigin", validOrigin)
				}
			} else {
				commit("setFlightOrigin", origin)
			}
		}
		else if (transportPacks.includes(packCode)) {
			// URL has no origin. Keep the cheapest origin WIYP already applied,
			// and write it back if a later hash update dropped it.
			const kept = state.flightOrigin?.split("-")[0] || ""
			if (kept && kept !== "NA" && kept !== "None") {
				setHash({ origin: kept })
			}
		}
		else {
			// If the origin is set and it is not a transport pack, set it to null
			commit("setFlightOrigin", null)
		}
	},

	checkActivePrestations: async ({ state, commit }) => {
		const hashStr = window.location.hash.replace("#", "")

		if (!hashStr.includes("activePrestations")) {
			return
		}

		const params = new URLSearchParams(hashStr)
		const activePrestations = params.get("activePrestations")

		if (!activePrestations) return

		try {
			const newActivePrestations = JSON.parse(activePrestations).map((prestId) =>
				String(prestId)
			)
			commit("setActivePrestations", newActivePrestations)

			const normalizeId = (id) => String(id)

			// Step 1: Filter the Prestations displayed on the page to include only the relevant ones
			const filteredPrestations = state.initialPrestationsArray.filter((prestation) =>
				state.activePrestations.includes(normalizeId(prestation.prestationId))
			)

			// Step 2: Extract all prestationIds from initialPrestationsArray
			const existingIds = state.initialPrestationsArray.map((prestation) =>
				normalizeId(prestation.prestationId)
			)

			// Step 3: Filter state.activePrestations for IDs not in existingIds
			const missingIds = state.activePrestations.filter(
				(id) => !existingIds.includes(normalizeId(id))
			)

			// Step 4: Create an array of promises, for the missingIds only
			const prestFromCatalogPromises = missingIds.map((prestationId) => {
				const requestData = {
					locale: store.getters.locale,
					prestationId,
				}
				return window.catalogService.getPrestationByLocale(requestData)
			})

			// Step 5: Resolve all promises in parallel
			const results = await Promise.all(prestFromCatalogPromises)

			// Step 6: allActivePrestations will include:
			// - Prestations currently displayed on the page that are part of activePrestations.
			// - Prestations from activePrestations that are not yet displayed on the page (fetched from the catalog).
			const allActivePrestations = [...filteredPrestations, ...results].filter(Boolean)

			// Step 7: sort allActivePrestations to match the order from state.activePrestations
			const orderedArray = allActivePrestations.sort((a, b) => {
				const indexA = state.activePrestations.indexOf(normalizeId(a.prestationId))
				const indexB = state.activePrestations.indexOf(normalizeId(b.prestationId))

				if (indexA === -1) return 1
				if (indexB === -1) return -1

				return indexA - indexB
			})

			commit("setPrestationsFromCatalog", orderedArray)
		} catch (error) {
			console.error("Error parsing activePrestations:", error)
		}
	},
}
